import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { MarginProps, margin } from 'styled-system'
import { colors } from 'utils/colors'
import { WidthProps } from 'types/styled'

import { ErrorMessage } from 'components/TextInput'
import Spinner from 'components/Spinner'
import Icon from '../../Icon'
import { CheckboxProps, CheckboxIconProps } from '../types'
import { StyledCheckbox } from '../IndeterminateCheckBox'

export const CheckboxIcon = ({ checked, disabled, indeterminate, isError }: CheckboxIconProps) => (
  <IconContainer checked={checked || indeterminate} disabled={disabled} isError={isError}>
    {indeterminate ? (
      <Icon icon={['fas', 'minus']} color={colors.white} fontSize={10} />
    ) : checked ? (
      <Icon icon={['fas', 'check']} color={colors.white} fontSize={10} />
    ) : undefined}
  </IconContainer>
)

// TODO: Add handling of state to toggle indeterminate state
// This will replace the redundant IndeterminateCheckbox component once implemented

const Checkbox = ({
  children,
  disabled,
  indeterminate,
  onChange,
  width,
  error,
  showError = true,
  isLoading = false,
  labelVerticalAlign = 'center',
  height,
  checked = false,
  name,
  ...props
}: CheckboxProps) => {
  const { t } = useTranslation()
  const text = typeof children === 'string' || children instanceof String ? t(children as string) : children

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.checked

    if (indeterminate && checked === true) {
      onChange?.(true)
    } else {
      onChange?.(value)
    }
  }

  return (
    <Container onClick={handleClick} width={width} {...props}>
      <CheckboxLabel
        checked={!!checked}
        disabled={disabled}
        error={error}
        verticalAlign={labelVerticalAlign}
        height={height}
      >
        <CheckboxIcon
          checked={checked}
          disabled={disabled}
          indeterminate={indeterminate}
          isError={showError && !!error}
        />
        {text}
        <StyledCheckbox type="checkbox" checked={checked} name={name} disabled={disabled} onChange={handleChange} />
      </CheckboxLabel>
      {showError && error && <ErrorMessage>{error}</ErrorMessage>}
      {isLoading && (
        <LoadingOverlay data-testid="checkbox-loading-overlay">
          <Spinner fontSize={16} />
        </LoadingOverlay>
      )}
    </Container>
  )
}

const Container = styled.div<MarginProps & { width?: WidthProps; disabled?: boolean; inline?: boolean }>`
  ${margin}
  position: relative;
  display: ${(p) => (p.inline ? 'inline-block' : 'block')};
  text-align: left;
  ${(p) => p.width && (!p.inline ? `width: ${p.width === 'auto' ? '100%' : `${p.width}px`};` : '')}
`

const CheckboxLabel = styled.label<{
  disabled?: boolean
  error?: string
  verticalAlign: string
  checked: boolean
  height?: string
}>`
  color: ${(p) =>
    p.error ? p.theme.colors.red : p.disabled && !p.checked ? p.theme.colors.gray4 : p.theme.colors.defaultText};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: ${(p) => p.verticalAlign};
  position: relative;
  min-height: ${(p) => p?.height ?? `1rem`};
  border: 1px solid transparent;
  user-select: text;
  ${(p) =>
    p.disabled &&
    css`
      div:first-child {
        cursor: not-allowed;
        pointer-events: none;
      }
    `}
  ${(p) =>
    !p.disabled &&
    css`
      &:hover {
        div:first-child {
          background: ${p.checked ? p.theme.colors.blue2 : p.theme.colors.gray4};
          border: 1px solid ${p.theme.colors.blue1};
        }
      }
    `}

  input {
    cursor: pointer;
    height: 0;
    width: 0;
    opacity: 0;
    position: absolute;
  }
`

const getBgColor = (p: { checked?: boolean; disabled?: boolean; isError?: boolean }) => {
  if (p.disabled) {
    return p.checked ? colors.blue1 : colors.gray4
  }

  if (p.isError) return colors.red

  return p.checked ? colors.blue1 : colors.gray3
}

const IconContainer = styled.div<{ checked?: boolean; disabled?: boolean; isError?: boolean }>`
  background: ${(p) => getBgColor(p)};
  border-radius: 3px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 0.5rem;
  height: 16px;
  width: 16px;
  min-width: 16px;
  min-height: 16px;
  text-align: center;
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

export default Checkbox
